import { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { Loader2, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Navigate, Link } from 'react-router-dom'
import Preloader from '@/components/Preloader'
import { useEnabledModules } from '@/hooks/useEnabledModules'
import { canAccess, type AccessRule, type AppRole } from '@/lib/authorization'


interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: AppRole
  allowedRoles?: AppRole[]
  accessRule?: AccessRule
}

const ProtectedRoute = ({ children, requiredRole, allowedRoles, accessRule }: ProtectedRouteProps) => {
  const { user, profile, loading, signOut } = useAuth()
  const { hasModule, loading: modulesLoading } = useEnabledModules()
  const [waitingForProfile, setWaitingForProfile] = useState(false)
  const [profileTimeout, setProfileTimeout] = useState(false)

  useEffect(() => {
    console.log('DEBUG PROTECTED ROUTE:', { 
      loading, 
      user: !!user, 
      profile: !!profile,
      profileRole: profile?.role,
      requiredRole,
      allowedRoles 
    });

    // Only act when loading is complete
    if (loading) return

    // No user - set waiting to false
    if (!user) {
      setWaitingForProfile(false)
      return
    }

    // User exists but no profile yet - wait for profile to load
    if (user && !profile) {
      if (!waitingForProfile) {
        setWaitingForProfile(true)
      }
      return
    }

    // Reset waiting state if profile loads
    if (user && profile && waitingForProfile) {
      setWaitingForProfile(false)
      setProfileTimeout(false)
    }
  }, [user, profile, loading, requiredRole, allowedRoles, waitingForProfile])

  // Set timeout for profile loading
  useEffect(() => {
    if (waitingForProfile && !profile) {
      const timer = setTimeout(() => {
        setProfileTimeout(true)
      }, 8000) // 8 seconds timeout
      
      return () => clearTimeout(timer)
    }
  }, [waitingForProfile, profile])

  // Show loading while authentication is in progress
  if (loading || modulesLoading) {
    return <Preloader />
  }

  // Don't render if no user (redirect is happening)
  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Don't render if user exists but no profile yet
  if (user && !profile) {
    if (profileTimeout) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-white">
          <div className="flex flex-col items-center space-y-6 max-w-md text-center">
            <RefreshCw className="h-12 w-12 text-orange-500" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Problema ao carregar perfil
              </h2>
              <p className="text-gray-600 mb-4">
                Estamos tendo dificuldades para carregar seus dados. Isso pode ser devido a uma conexão lenta.
              </p>
            </div>
            <div className="space-y-3">
              <Button 
                onClick={() => {
                  setProfileTimeout(false)
                  setWaitingForProfile(false)
                  window.location.reload()
                }}
                className="w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Tentar novamente
              </Button>
              <Button 
                onClick={async () => {
                  setProfileTimeout(false)
                  setWaitingForProfile(false)
                  await signOut()
                }}
                variant="outline" 
                className="w-full"
              >
                Voltar ao login
              </Button>
            </div>
          </div>
        </div>
      )
    }
    
    return <Preloader />
  }

  // Role checks after profile is present
  if (requiredRole && profile?.role !== requiredRole) {
    return <Navigate to="/dashboard" replace />
  }
  if (allowedRoles && profile && !allowedRoles.includes(profile.role)) {
    return <Navigate to="/dashboard" replace />
  }
  if (accessRule && !canAccess({ role: profile?.role, hasModule }, accessRule)) {
    return <Navigate to="/dashboard" replace />
  }
  return <>{children}</>
}

export default ProtectedRoute
